import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  TextField,
  MenuItem,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useParams } from "react-router-dom";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, toastError } from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { MuiTelInput } from "mui-tel-input";
import LayoutContainer from "../widgets/LayoutContainer";

const WelcomeInviteMember = () => {
  const { id } = useParams(); // The token from URL params

  const [profileImage, setProfileImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [name, setName] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [upLoading, setUpLoading] = useState(false);
  const [logos, setLogos] = useState();
  const [companyName, setCompanyName] = useState();
  const [verificationMessage, setVerificationMessage] = useState(null);
  const [verificationError, setVerificationError] = useState(null);

  // Verify token when component mounts
  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${API_ENDPOINT}user/verify-member-invite-token?id=${id}`
        );
        setCompanyName(data.companyName);
        setLogos(data.logos);
        setVerificationMessage(data.message);
        loadDesignations(id);
      } catch (error) {
        setVerificationError(getErrorResponse(error));
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [id]);

  async function loadDesignations(token) {
    try {
      const { data } = await axios.get(
        `${API_ENDPOINT}user/get-designations-member?id=${token}`
      );
      setRoles(data);
    } catch (error) {
      toastError(error);
    }
  }

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !phone || !profileImage) {
      toast.error("All fields are required.");
      return;
    }
    try {
      setUpLoading(true);
      const formData = new FormData();
      formData.append("image", selectedFile); // The selected image file
      formData.append("name", name);
      formData.append("phone", phone.trim());
      const { data } = await axios.post(
        `${API_ENDPOINT}user/register-member?id=${id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setRequestSuccess(true);
      toast.success(data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setUpLoading(false);
    }

    // Handle submission logic (API call, etc.)
  };

  return (
    <LayoutContainer>
      {" "}
      {requestSuccess ? (
        <Container maxWidth="sm">
          <Alert severity="success">
            <AlertTitle>Profile Created Successfully</AlertTitle>
            Your profile has been created successfully! You can now log in to
            the <strong>BetaZen App</strong>. Before logging in, please set your
            account password using the link sent to your email.
            <strong>Check your inbox</strong> and follow the instructions to
            complete the setup.
          </Alert>
        </Container>
      ) : (
        <Container maxWidth="sm">
          <Paper sx={{ maxWidth: "450px", mx: "auto", mt: 5 }}>
            {/* Banner and Logo Section */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 150, // Fixed height for 3:1 banner
                mt: 2,
                mb: -8, // Overlap with the content box
              }}
            >
              {logos?.bnLogo && (
                <Box
                  component="img"
                  src={logos.bnLogo}
                  alt="BetaZen banner"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "8px 8px 0 0",
                    boxShadow: 2,
                  }}
                />
              )}
              {logos?.srLogo && (
                <Avatar
                  src={logos.srLogo}
                  alt="BetaZen logo"
                  sx={{
                    width: 120,
                    height: 120,
                    position: "absolute",
                    bottom: -60, // Half of the avatar hangs below the banner
                    left: "50%",
                    transform: "translateX(-50%)",
                    border: "4px solid white",
                    bgcolor: "grey.100",
                    boxShadow: 3,
                  }}
                />
              )}
            </Box>

            {/* Form Section */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              width="100%"
              mx="auto"
              mt={8} // Adjusted to account for banner overlap
              textAlign="center"
              p={3}
              borderRadius={2}
              bgcolor="white"
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color="primary"
                gutterBottom
                sx={{ mt: 6 }}
              >
                Welcome to {companyName}!
              </Typography>
              <Typography variant="body1" color="textSecondary" mb={3}>
                Join our community and set up your profile to get started.
              </Typography>

              {/* Loading Indicator while Verifying Token */}
              {loading && (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress />
                </Box>
              )}

              {/* Verification Error Message */}
              {verificationError && (
                <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
                  {verificationError}
                </Alert>
              )}

              {/* Hide form if token is invalid */}
              {!loading && !verificationError && (
                <Stack spacing={2} width="100%">
                  {/* Profile Image Upload */}
                  <Stack spacing={2} alignItems="center">
                    <Avatar
                      src={profileImage}
                      sx={{
                        width: 100,
                        height: 100,
                        bgcolor: "primary.light",
                        fontSize: "2rem",
                      }}
                    >
                      {!profileImage && "?"}
                    </Avatar>
                    <Button
                      size="small"
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Profile Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                  </Stack>

                  {/* Name Input */}
                  <TextField
                    size="small"
                    fullWidth
                    label="Name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />

                  {/* Phone Input */}
                  <MuiTelInput
                    fullWidth
                    size="small"
                    label="Phone"
                    defaultCountry="IN"
                    forceCallingCode
                    value={phone}
                    onChange={(newValue) => setPhone(newValue)}
                  />

                  {/* Submit Button */}
                  <LoadingButton
                    fullWidth
                    loading={upLoading}
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!name || !phone || !profileImage}
                    sx={{ borderRadius: 20, py: 1.5 }}
                  >
                    Join {companyName}
                  </LoadingButton>
                </Stack>
              )}
            </Box>
          </Paper>
        </Container>
      )}
    </LayoutContainer>
  );
};

export default WelcomeInviteMember;
